import React, { useState } from "react";
import { isValidUrl, sanitizeUrl } from "../utils/urlValidation";
import { sanitizeTitle, sanitizeTag } from "../utils/inputSanitization";
import { COLOR_PRESETS } from "../utils/adaptiveColor";

interface AddLinkCardProps {
  onAdd: (data: {
    url: string;
    name?: string;
    tags?: string[];
    bgColor?: string;
    col?: number;
    createdDate?: number;
  }) => void;
  onDismiss: () => void;
  loading: boolean;
  editData?: { url: string; name?: string; tags?: string[]; bgColor?: string } | null;
  onCancelEdit?: () => void;
  hideCancel?: boolean;
}

const AddLinkCard: React.FC<AddLinkCardProps> = ({
  onAdd,
  onDismiss,
  loading,
  editData,
  onCancelEdit,
  hideCancel,
}) => {
  const [editing, setEditing] = useState(!!editData);
  const [url, setUrl] = useState(editData?.url || "");
  const [title, setTitle] = useState(editData?.name || "");
  const [tags, setTags] = useState<string[]>(editData?.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [bgColor, setBgColor] = useState(editData?.bgColor || "");
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [error, setError] = useState("");

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    let urlToSave = url.trim();

    // Accept bare domains and add https://
    if (!/^https?:\/\//i.test(urlToSave)) {
      if (/^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i.test(urlToSave)) {
        urlToSave = "https://" + urlToSave;
      } else {
        setError("Please enter a valid URL");
        return;
      }
    }

    // Validate URL for security
    if (!isValidUrl(urlToSave)) {
      setError("This URL appears to be invalid or potentially unsafe");
      return;
    }

    // Sanitize the URL
    urlToSave = sanitizeUrl(urlToSave);
    if (!urlToSave) {
      setError("Unable to process this URL");
      return;
    }

    let resolvedTitle = sanitizeTitle(title);
    if (!resolvedTitle) {
      resolvedTitle = urlToSave;
    }

    // Sanitize all tags
    const sanitizedTags = tags.map((tag) => sanitizeTag(tag)).filter((tag) => tag.length > 0);

    onAdd({
      url: urlToSave,
      name: resolvedTitle,
      tags: sanitizedTags,
      bgColor: bgColor || undefined,
    });
    setUrl("");
    setTitle("");
    setTags([]);
    setTagInput("");
    setBgColor("");
    setShowBgPicker(false);
    setEditing(false);
    if (onCancelEdit) onCancelEdit();
    // REMOVED: if (onDismiss) onDismiss(); - Don't auto-close for bulk operations
  }

  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === " " || e.key === "Enter") && tagInput.trim()) {
      let cleanTag = tagInput.trim();
      if (cleanTag.startsWith("#")) cleanTag = cleanTag.slice(1);
      cleanTag = sanitizeTag(cleanTag);
      if (cleanTag && !tags.includes(cleanTag)) {
        setTags([...tags, cleanTag]);
      }
      setTagInput("");
      e.preventDefault();
    }
  };

  const handleTagInputBlur = () => {
    let cleanTag = tagInput.trim();
    if (cleanTag.startsWith("#")) cleanTag = cleanTag.slice(1);
    cleanTag = sanitizeTag(cleanTag);
    if (cleanTag && !tags.includes(cleanTag)) {
      setTags([...tags, cleanTag]);
    }
    setTagInput("");
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <label
          htmlFor="url-input"
          className="block text-sm font-medium text-[var(--color-text-primary)] mb-2"
        >
          Link URL
        </label>
        <input
          id="url-input"
          type="text"
          className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
        />
      </div>

      <div>
        <label
          htmlFor="title-input"
          className="block text-sm font-medium text-[var(--color-text-primary)] mb-2"
        >
          Display Name (optional)
        </label>
        <input
          id="title-input"
          type="text"
          className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent"
          placeholder="My Website"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
          Tags (optional)
        </label>
        <div className="flex flex-wrap gap-2 items-center min-h-[38px] p-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-bg-secondary)]">
          {tags.map((tag, i) => (
            <span
              key={i}
              className="bg-[var(--color-bg-hover)] border border-[var(--color-accent)] text-[var(--color-accent)] rounded-md px-2 py-1 text-xs font-medium flex items-center"
            >
              {tag}
              <button
                type="button"
                className="ml-1 text-sm leading-none text-[var(--color-accent)] hover:text-[var(--color-danger)] focus:outline-none"
                onClick={() => setTags(tags.filter((t) => t !== tag))}
                tabIndex={-1}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            className="flex-1 min-w-[60px] px-2 py-1 rounded bg-transparent text-[var(--color-text-primary)] text-xs focus:outline-none placeholder:text-[var(--color-text-tertiary)]"
            placeholder="Add tag…"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagInput}
            onBlur={handleTagInputBlur}
          />
        </div>
      </div>

      {/* Background color picker */}
      <div>
        <button
          type="button"
          onClick={() => setShowBgPicker(!showBgPicker)}
          className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <div
            className="w-4 h-4 rounded border border-[var(--color-border)]"
            style={{ backgroundColor: bgColor || "var(--color-bg-secondary)" }}
          />
          <span>Label color</span>
          {bgColor && (
            <span
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-danger)] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setBgColor("");
              }}
            >
              clear
            </span>
          )}
        </button>
        {showBgPicker && (
          <div
            className="mt-2 grid gap-0.5 rounded-md p-1.5 bg-[var(--color-bg)] border border-[var(--color-border)]"
            style={{ gridTemplateColumns: "repeat(11, 1fr)" }}
          >
            {COLOR_PRESETS.map((color, i) => (
              <button
                key={i}
                type="button"
                className={`w-3 h-3 rounded-sm transition-transform hover:scale-150 hover:z-10
                  ${color === bgColor ? "ring-1 ring-[var(--color-accent)] scale-150 z-10" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => setBgColor(color)}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-[var(--color-danger)] bg-[var(--color-bg-hover)] border border-[var(--color-danger)] rounded-lg p-3">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        {!hideCancel && (
          <button
            type="button"
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border border-[var(--color-border)] text-[var(--color-text-primary)] bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
            onClick={() => {
              setEditing(false);
              setUrl("");
              setTitle("");
              setTags([]);
              setTagInput("");
              setBgColor("");
              setShowBgPicker(false);
              setError("");
              if (onCancelEdit) onCancelEdit();
              if (onDismiss) onDismiss();
            }}
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-1
            ${loading ? "bg-[var(--color-bg-hover)] text-[var(--color-text-tertiary)] cursor-not-allowed" : "bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-text-inverse)]"}`}
          disabled={loading || !url.trim()}
        >
          {loading ? "Adding…" : editing ? "Save Changes" : "Add Link"}
        </button>
      </div>
    </form>
  );
};

export default AddLinkCard;
